import React, { useState, useEffect } from 'react';
import apiClient from '@/utils/api';
import { Target, CalendarClock, TrendingUp } from 'lucide-react';

/**
 * Company admission-target progress + day countdown for the active intake.
 * Period-only: admissions converted + active (not cancelled) within the intake window.
 * `showMine` renders the counsellor's own contribution to the shared company target.
 */
export const IntakeProgressCard = ({ showMine = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    apiClient.get('/dashboard/intake-progress')
      .then((r) => { if (active) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  if (loading || !data || !data.intake) return null;

  const { intake, admissions, target, remaining, per_day_needed, days } = data;
  const mine = data.your_admissions;
  const hasTarget = target !== null && target !== undefined;
  const pct = hasTarget && target > 0 ? Math.min(100, Math.round((admissions / target) * 100)) : 0;
  const dayPct = days && days.total > 0 ? Math.min(100, Math.round((days.passed / days.total) * 100)) : 0;

  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm"
      data-testid="intake-progress-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
            <Target className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-900 truncate">{intake.name}</h3>
            <p className="text-[11px] text-slate-500">Company admission target</p>
          </div>
        </div>
        {days && (
          <span
            className={`text-[11px] font-semibold px-2 py-1 rounded-full ${days.is_closed ? 'bg-slate-100 text-slate-500' : days.left <= 14 ? 'bg-red-100 text-red-700' : 'bg-teal-50 text-teal-700'}`}
            data-testid="intake-days-left"
          >
            {days.is_closed ? 'Intake closed' : `${days.left} days left`}
          </span>
        )}
      </div>

      {/* Days bar */}
      {days && (
        <div className="mb-4">
          <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-1">
            <CalendarClock className="w-3.5 h-3.5" />
            <span>{days.total} days total · {days.passed} passed · <b className="text-slate-700">{days.left} left</b> to close</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full bg-slate-400 transition-all" style={{ width: `${dayPct}%` }} />
          </div>
        </div>
      )}

      {/* Admissions bar */}
      <div>
        <div className="flex items-end justify-between mb-1">
          <span className="text-[11px] text-slate-500">Admissions this period</span>
          <span className="text-lg font-bold text-slate-900" data-testid="intake-admissions-count">
            {admissions}{hasTarget && <span className="text-sm font-medium text-slate-400"> / {target}</span>}
          </span>
        </div>
        {hasTarget ? (
          <>
            <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 transition-all" style={{ width: `${pct}%` }} />
            </div>
            <p className="text-[11px] text-slate-600 mt-1.5 flex items-center gap-1">
              <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
              {days && !days.is_closed && days.left > 0
                ? <span><b>{remaining} more to do in {days.left} days</b>{per_day_needed ? ` (~${per_day_needed}/day)` : ''}</span>
                : (remaining > 0 ? <span><b>{remaining} more to reach target</b></span> : <span className="text-emerald-700 font-semibold">Target reached! 🎉</span>)}
            </p>
          </>
        ) : (
          <p className="text-[11px] text-amber-600 mt-1">Set an Admission Target on this intake to track progress.</p>
        )}
        {showMine && mine !== undefined && (
          <p className="text-[11px] text-slate-600 mt-2 pt-2 border-t border-slate-100" data-testid="intake-your-admissions">
            Your admissions this period: <b className="text-slate-900">{mine}</b>
          </p>
        )}
      </div>
    </div>
  );
};

export default IntakeProgressCard;
